import React from 'react';
import { Field } from 'redux-form';
import Label from './inputs/Label';
import { checkBoxInline, textFieldInline } from './inputs';
import { masks, noop, renderError, validations } from '../helperFunctions';

const { required, isNotNull } = validations;

const NotificationField = ({
  wrapperClasses = {},
  fieldData = {},
  disabled = false,
  onChange = noop,
  notificationPreferences = [],
  fieldFormData = {},
  namePrefix = '',
}) => {
  const hasPreferences =
    notificationPreferences && Object.keys(notificationPreferences).length > 0;

  const runValidations = (value, validators = []) => {
    let error = null;
    validators.forEach((validate) => {
      if (!error) {
        const result = validate(value);
        if (result) error = result;
      }
    });
    return error;
  };

  const buildComboFieldValidator = (type) => {
    const formatValidator = (value) => {
      if (type === 'email') {
        return validations.email(value);
      }
      if (type === 'phone') {
        return validations.phone(value, 'en', 'valid_phone_number_count');
      }
      return null;
    };

    return (value, allValues) => {
      if (!hasPreferences) {
        // No preferences, validate required + format if value is present
        const validators = value ? [required, formatValidator] : [required];
        return runValidations(value, validators);
      }

      const checkboxNames = Object.keys(notificationPreferences)
        .map((key) => `${namePrefix}${notificationPreferences[key]?.name}`)
        .filter(Boolean);

      const hasAnyChecked = checkboxNames.some(
        (checkboxName) => allValues[checkboxName],
      );

      // If any checkbox is checked → required + format (if value exists)
      // If no checkbox is checked → only validate format if there's a value
      if (hasAnyChecked) {
        return runValidations(value, [required, formatValidator]);
      }
      if (value) {
        return runValidations(value, [formatValidator]);
      }

      return null; // no validation needed
    };
  };

  const comboFieldValidator = {
    email: buildComboFieldValidator('email'),
    phone: buildComboFieldValidator('phone'),
  };

  const RenderErrorRow = ({ error }) =>
    isNotNull(error) ? (
      <tr className={`${wrapperClasses?.validationRow} notif-tr`}>
        <td className="notif-td" />
        <td
          colSpan={
            hasPreferences ? Object.keys(notificationPreferences).length : 1
          }
          className="notif-td"
        >
          <span
            dangerouslySetInnerHTML={{
              __html: error,
            }}
            className="error"
          />
        </td>
      </tr>
    ) : null;

  return (
    <React.Fragment>
      <tr className="notif-tr">
        <td className={`${wrapperClasses?.labelCol} notif-td`}>
          <Label label={fieldData.label} />
        </td>
        <td className={`${wrapperClasses?.inputCol} notif-td`}>
          <Field
            placeholder={fieldData.placeholder}
            name={`${namePrefix}${fieldData.name}`}
            label={null}
            component={textFieldInline}
            containerClass={wrapperClasses?.inputContainer}
            onChange={onChange}
            disabled={fieldData.disabled || disabled}
            normalize={masks[fieldData.mask]}
            hideError
          />
        </td>
        {hasPreferences &&
          Object.keys(notificationPreferences)?.map((key) => {
            const preference = notificationPreferences[key];
            const isDisabled =
              fieldFormData.inValid ||
              validations.isNull(fieldFormData?.value) ||
              (preference.disabled &&
                fieldFormData?.value === fieldData.default);
            return (
              <td
                key={key}
                className={`${wrapperClasses?.checkboxCol} checkbox-col notif-td`}
              >
                <Field
                  name={`${namePrefix}${preference.name}`}
                  component={checkBoxInline}
                  label=""
                  skipPristine
                  wrapperClass={wrapperClasses?.checkbox}
                  onChange={onChange}
                  touchOnChange
                  defaultValue={isDisabled ? false : preference.default_checked}
                  disabled={disabled || isDisabled}
                />
              </td>
            );
          })}
      </tr>
      <tr className={`${wrapperClasses?.validationRow} notif-tr`}>
        <td className="notif-td" />
        <td
          colSpan={
            notificationPreferences
              ? Object.keys(notificationPreferences).length + 1
              : 1
          }
          className="notif-td"
        >
          <Field
            validate={[comboFieldValidator[fieldData.combo_type]]}
            name={`${namePrefix}${fieldData.name}`}
            component={renderError}
            wrapperClass="error-field-wrapper"
          />
        </td>
      </tr>
      {fieldData?.errors &&
        Object.entries(fieldData.errors).map(([key, message]) => {
          const isOptedOutError = key === 'opted_out';
          const hasChangedValue = fieldFormData?.value !== fieldData.default;
          const shouldDisplayError = !(isOptedOutError && hasChangedValue);

          return shouldDisplayError ? (
            <RenderErrorRow error={message} key={key} />
          ) : null;
        })}
      <tr className={`${wrapperClasses.spacerRow} notif-tr`}>
        <td className="notif-td" />
      </tr>
    </React.Fragment>
  );
};

export default NotificationField;
